import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import HeadlineBlock from './blocks/HeadlineBlock';
import ParagraphBlock from './blocks/ParagraphBlock';
import ImageBlock from './blocks/ImageBlock';
import VideoBlock from './blocks/VideoBlock';
import BlockquoteBlock from './blocks/BlockquoteBlock';
import CodeBlock from './blocks/CodeBlock';
import EmbedBlock from './blocks/EmbedBlock';
import { parseIframeHtml, isAllowedSrc } from './blocks/EmbedBlock';
import BlockToolbar from './BlockToolbar';
import {
  Bars3Icon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const BLOCK_COMPONENTS = {
  h2: HeadlineBlock,
  h3: HeadlineBlock,
  paragraph: ParagraphBlock,
  image: ImageBlock,
  video: VideoBlock,
  blockquote: BlockquoteBlock,
  code: CodeBlock,
  embed: EmbedBlock,
};

const BlockEditor = ({ blocks = [], onChange, language = 'en', className = '' }) => {
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [showToolbar, setShowToolbar] = useState(null); // Block ID to show toolbar after
  const isRTL = language === 'he';

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const items = Array.from(blocks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onChange(items);
  }, [blocks, onChange]);

  const updateBlock = useCallback((blockId, updates) => {
    const updatedBlocks = blocks.map(block =>
      block.id === blockId
        ? { ...block, content: { ...block.content, ...updates } }
        : block
    );
    onChange(updatedBlocks);
  }, [blocks, onChange]);

  const deleteBlock = useCallback((blockId) => {
    onChange(blocks.filter(block => block.id !== blockId));
    setActiveBlockId(null);
  }, [blocks, onChange]);

  const addBlock = useCallback((type, afterBlockId = null) => {
    const newBlock = {
      id: uuidv4(),
      type,
      content: getDefaultContent(type)
    };

    let updatedBlocks;
    if (afterBlockId) {
      const index = blocks.findIndex(b => b.id === afterBlockId);
      updatedBlocks = [
        ...blocks.slice(0, index + 1),
        newBlock,
        ...blocks.slice(index + 1)
      ];
    } else {
      updatedBlocks = [...blocks, newBlock];
    }

    onChange(updatedBlocks);
    setShowToolbar(null);
    
    // Focus the new block after a short delay
    setTimeout(() => {
      setActiveBlockId(newBlock.id);
    }, 100);
  }, [blocks, onChange]);

  const getDefaultContent = (type) => {
    switch (type) {
      case 'h2':
        return { text: 'New Headline', level: 2 };
      case 'h3':
        return { text: 'New Subheadline', level: 3 };
      case 'paragraph':
        return { text: 'Enter your text here...' };
      case 'image':
        return { url: '', alt: '', caption: '' };
      case 'video':
        return { url: '', aspectRatio: '16:9' };
      case 'blockquote':
        return { text: 'Enter quote...', author: '' };
      case 'code':
        return { code: '', language: 'javascript' };
      case 'embed':
        return { src: '', title: '', width: '', height: '', caption: '' };
      default:
        return {};
    }
  };

  const parseHtmlToBlocks = useCallback((html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const newBlocks = [];

    const iframes = temp.querySelectorAll('iframe');
    if (iframes.length === 0) return null;

    iframes.forEach((iframe) => {
      const parsed = parseIframeHtml(iframe.outerHTML);
      if (!parsed || !parsed.src) return;
      if (!isAllowedSrc(parsed.src)) return;

      newBlocks.push({
        id: uuidv4(),
        type: 'embed',
        content: {
          src: parsed.src,
          title: parsed.title,
          width: parsed.width,
          height: parsed.height,
          sandbox: parsed.sandbox,
          allow: parsed.allow,
          allowFullscreen: parsed.allowFullscreen,
          style: parsed.style,
          caption: '',
        },
      });
    });

    const visibleParagraphs = temp.querySelectorAll('p');
    visibleParagraphs.forEach((p) => {
      let el = p;
      while (el.parentElement && el.parentElement !== temp) {
        el = el.parentElement;
        if (el.style?.display === 'none' || el.getAttribute?.('style')?.includes('display: none') || el.getAttribute?.('style')?.includes('display:none')) return;
        if (el.hidden) return;
      }
      const text = p.textContent?.trim();
      if (!text) return;
      newBlocks.push({
        id: uuidv4(),
        type: 'paragraph',
        content: { html: p.innerHTML.trim(), text },
      });
    });

    return newBlocks.length > 0 ? newBlocks : null;
  }, []);

  const handleEditorPaste = useCallback((e) => {
    const target = e.target;
    if (target.closest?.('.embed-block')) return;

    const html = e.clipboardData?.getData('text/html');
    const plain = e.clipboardData?.getData('text/plain') || '';
    const textToCheck = html || plain;
    if (!textToCheck || !textToCheck.includes('<iframe')) return;

    const parsed = parseHtmlToBlocks(textToCheck);
    if (!parsed || parsed.length === 0) return;

    e.preventDefault();
    e.stopPropagation();
    onChange([...blocks, ...parsed]);
  }, [blocks, onChange, parseHtmlToBlocks]);

  const renderBlock = (block, index) => {
    const BlockComponent = BLOCK_COMPONENTS[block.type];
    
    if (!BlockComponent) {
      return (
        <div className="p-4 bg-red-50 text-red-600 rounded">
          Unknown block type: {block.type}
        </div>
      );
    }

    return (
      <Draggable key={block.id} draggableId={block.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`group relative mb-4 ${
              snapshot.isDragging ? 'opacity-75' : ''
            } ${activeBlockId === block.id ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}
          >
            {/* Block Controls */}
            <div className="absolute -left-10 top-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                {...provided.dragHandleProps}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="Drag to reorder"
              >
                <Bars3Icon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowToolbar(showToolbar === block.id ? null : block.id)}
                className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                title="Add block"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => deleteBlock(block.id)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Delete block"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Block Content */}
            <div
              onClick={() => setActiveBlockId(block.id)}
              className="cursor-text"
            >
              <BlockComponent
                block={block}
                isActive={activeBlockId === block.id}
                onChange={(updates) => updateBlock(block.id, updates)}
                isRTL={isRTL}
                language={language}
              />
            </div>

            {/* Add Block Toolbar */}
            {showToolbar === block.id && (
              <div className="mt-2">
                <BlockToolbar
                  onAddBlock={(type) => addBlock(type, block.id)}
                  onClose={() => setShowToolbar(null)}
                />
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className={`block-editor ${className}`} onPaste={handleEditorPaste}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="blocks">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="min-h-[200px] pl-12"
            >
              {blocks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="mb-4">Start writing your article...</p>
                  <BlockToolbar
                    onAddBlock={(type) => addBlock(type)}
                    onClose={() => {}}
                    inline
                  />
                </div>
              ) : (
                blocks.map((block, index) => renderBlock(block, index))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add Block Button at the end */}
      {blocks.length > 0 && (
        <div className="pl-12 mt-4">
          {showToolbar === 'end' ? (
            <BlockToolbar
              onAddBlock={(type) => addBlock(type)}
              onClose={() => setShowToolbar(null)}
            />
          ) : (
            <button
              onClick={() => setShowToolbar('end')}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Add Block
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BlockEditor;
